/**
 * Helper functions for interacting with the AppSidebar in tour guides.
 * The new AppSidebar uses hover/click to open a submenu panel instead of
 * collapsible sections. These helpers click the main nav icon and wait
 * for the submenu link to appear.
 */

/**
 * Clicks a main sidebar navigation icon and waits for a submenu link to appear.
 * @param {string} mainNavSelector - Selector for the main navigation icon (e.g., '[data-tour="settingsLink"]')
 * @param {string} submenuSelector - Selector for the submenu link to wait for (e.g., '[data-tour="messagingSettingsLink"]')
 * @param {number} timeout - Maximum time to wait in ms (default: 2000)
 * @returns {Promise<void>}
 */
export const openSidebarSubmenu = async (mainNavSelector, submenuSelector, timeout = 2000) => {
  const mainNavElement = document.querySelector(mainNavSelector);
  if (mainNavElement) {
    // Trigger both click and mouseenter to ensure the panel opens
    mainNavElement.click();
    mainNavElement.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
  }

  // Wait for the submenu element to appear
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkElement = () => {
      const submenuElement = document.querySelector(submenuSelector);
      if (submenuElement) {
        resolve();
        return;
      }

      if (Date.now() - startTime < timeout) {
        requestAnimationFrame(checkElement);
      } else {
        // Resolve anyway after timeout to not block the tour
        resolve();
      }
    };

    checkElement();
  });
};

/**
 * Pre-configured helpers for common sidebar sections
 */
export const openSettingsSubmenu = (submenuSelector) =>
  openSidebarSubmenu('[data-tour="settingsLink"]', submenuSelector);

export const openCampaignsSubmenu = (submenuSelector) =>
  openSidebarSubmenu('[data-tour="campaignLink"]', submenuSelector);

export const openWorkflowsSubmenu = (submenuSelector) =>
  openSidebarSubmenu('[data-tour="workFlowsLink"]', submenuSelector);

export const openMarketingChannelsSubmenu = (submenuSelector) =>
  openSidebarSubmenu('[data-tour="marketingChannelsLink"]', submenuSelector);

export const openDataPlatformSubmenu = (submenuSelector) =>
  openSidebarSubmenu('[data-tour="dataPlatformLink"]', submenuSelector);

export const openPersonalizationSubmenu = (submenuSelector) =>
  openSidebarSubmenu('[data-tour="webPersonalizationLink"]', submenuSelector);

export const openAnalyticsSubmenu = (submenuSelector) =>
  openSidebarSubmenu('[data-tour="analyticsLink"]', submenuSelector);

/**
 * Closes the sidebar panel by clicking on it, which triggers the handleLinkClick handler.
 * Also hides any lingering Mantine tooltips.
 * Call this after navigating away from the sidebar to prevent it from overlaying the main content.
 */
export const closeSidebarPanel = () => {
  // Find the sidebar panel using data-tour attribute and click it to close
  const sidebarPanel = document.querySelector('[data-tour="sidebarPanel"]');
  if (sidebarPanel) {
    sidebarPanel.click();
  }

  // Hide any visible Mantine tooltips by finding and hiding their portal elements
  const tooltips = document.querySelectorAll('.mantine-Tooltip-tooltip');
  tooltips.forEach((tooltip) => {
    tooltip.style.display = 'none';
  });

  // Trigger mouseleave on all sidebar main link buttons to dismiss hover states
  const sidebarButtons = document.querySelectorAll('[data-tour="sidebarContainer"] button');
  sidebarButtons.forEach((button) => {
    button.dispatchEvent(
      new MouseEvent('mouseleave', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );
  });
};
